/* RESPONSIBLE TEAM: team-standalone */

import { calculateHighestIntegrationState as calcState, IntegrationState } from '../constants';
import type { StandalonePlatform } from '../constants';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { setOwner } from '@ember/application';
import type ApplicationInstance from '@ember/application/instance';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import type {
  ConversationExtractionRowData,
  KnowledgeHubModalData,
  ConversationExcerptData,
  OnboardingGuideStepData,
  RequestAccessData,
  ChannelTargetingBodyData,
  AiContentLibraryData,
  KnowledgeContentData,
  ComposerOverflowMenuTooltipData,
  ExampleCodeSnippetData,
  ConversationIngestionData,
} from './component-data/definitions';
import { type UserField } from 'embercom/objects/standalone/data-fields/zendesk-user-field';
import { type OrganizationField } from 'embercom/objects/standalone/data-fields/zendesk-organization-field';
import { type TicketField } from 'embercom/objects/standalone/data-fields/zendesk-ticket-field';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';

export default abstract class Platform {
  @service declare intl: IntlService;

  // Not used by Generic system platforms who define a platform-specific Fin Messenger config.
  intercomMessengerConfig?: IntercomMessengerConfiguration;

  constructor(owner: ApplicationInstance) {
    setOwner(this, owner);
  }

  abstract get connectionState(): IntegrationState;

  calculateHighestIntegrationState(states: IntegrationState[]) {
    return calcState(states);
  }

  // Never make keys optional. If you do not need to implement specific component data for a given platform, you should return null.
  // This makes it easier for us to know which components a platform actually has implementations for.
  abstract get componentData(): {
    'conversation-extraction': ConversationExtractionRowData | null;
    'knowledge-hub-modal': KnowledgeHubModalData | null;
    'conversation-excerpt': ConversationExcerptData | null;
    'onboarding-guide-step': OnboardingGuideStepData | null;
    'request-access': RequestAccessData | null;
    'channel-targeting-body': ChannelTargetingBodyData | null;
    'ai-content-library': AiContentLibraryData | null;
    'knowledge-content': KnowledgeContentData | null;
    'composer-overflow-menu-tooltip': ComposerOverflowMenuTooltipData | null;
    'example-code-snippet': ExampleCodeSnippetData | null;
    'conversation-ingestion': ConversationIngestionData | null;
  };

  abstract get isConnected(): boolean;
  abstract get hasInstalledCopilot(): boolean;
  abstract get supportsHistoricalSyncing(): boolean;
  abstract get historicalSyncingEnabled(): boolean;
  abstract get finIsLive(): boolean;
  abstract get finIsPaused(): boolean;
  // If returning true, add expected errors to error-details-sheet.tsx
  abstract get supportsErrorLogs(): boolean;
  abstract get icon(): InterfaceIconName;
  abstract get platformKey(): StandalonePlatform;
  abstract get showOauthBanner(): boolean;
  abstract get supportsConversationSyncing(): boolean;
  abstract get supportsFinPlayground(): boolean;

  /**
   * Whether this platform has the messenger settings tab under
   * Settings > Channels > Messenger. Override to `true` (gated by
   * the platform's own feature flag) to opt in.
   */
  get hasMessengerSettings(): boolean {
    return false;
  }

  // This is a minimal method that should be implemented by all platforms to load data about the current state of the platform.
  // It is used in certain UI locations outside of `apps.app.standalone` where we haven't loaded all the data for the standalone platform yet,
  // e.g. the page where an agent must request access to the platform.
  abstract loadConfigData(): Promise<void>;

  // This is a minimal method that should be implemented by all platforms to load data about synced data fields.
  // It's used on the settings pages which are outside the `apps.app.standalone` route.
  abstract loadDataFields(): Promise<void>;

  // Load the data required by the pages under `apps.app.standalone`
  abstract loadRequiredData(): Promise<void>;

  // Used in workflows to know which editor to open when selecting an existing conversation field.
  abstract findMappedConversationField(
    id: string | undefined,
  ): TicketField | ExternalFieldIdentifier | undefined;

  abstract isMappedUserAttribute(attributeIdentifier: string): boolean;
  abstract isMappedCompanyAttribute(attributeIdentifier: string): boolean;
  abstract isMappedConversationAttribute(attributeIdentifier: string): boolean;

  // TODO: refactor these to always use `ExternalFieldIdentifier`
  abstract get ticketFields(): TicketField[] | ExternalFieldIdentifier[]; // Use ExternalFieldIdentifier for new platforms.
  abstract get userFields(): UserField[] | ExternalFieldIdentifier[]; // Use ExternalFieldIdentifier for new platforms.
  abstract get companyFields(): OrganizationField[] | ExternalFieldIdentifier[]; // Use ExternalFieldIdentifier for new platforms.

  // TODO: Do we really need this?
  get finIsNotLiveAndIsPaused() {
    return !this.finIsLive && this.finIsPaused;
  }

  get showAnalyzeSectionConnectCta() {
    return [IntegrationState.NotConfigured, IntegrationState.Created].includes(
      this.connectionState,
    );
  }

  get canUseReactStepBasedIA(): boolean {
    return false;
  }
}
